import { Alert, App, Button, Card, Col, Descriptions, Modal, Popconfirm, Row, Typography } from 'antd';
import { useLanguage } from '../../utils/LanguageContext';
import accountTranslations from '../AccountSettings/AccountSettings.lang.json';
import translations from '../ComponentsTranslations.lang.json';
import './CommonComponents.css';
import { useBooleanState } from './commonHooks';
import schemaTranslations from '../../shared/validations/allTranslations.lang.js';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const NavigateButton = ({ to, children, ...props }) => {
  const navigate = useNavigate();
  return (
    <Button onClick={() => navigate(to)} {...props}>
      {children}
    </Button>
  );
};

const Explainer = ({ question, answer }) => {
  const modalOpen = useBooleanState(false);

  return (
    <>
      <Modal title={question} open={modalOpen.value} onCancel={modalOpen.setFalse} footer={null}>
        <p>{answer}</p>
        {/* <div dangerouslySetInnerHTML={{ __html: answer }} /> */}
      </Modal>
      <p>
        <Button type="link" role="link" onClick={modalOpen.setTrue}>
          {question}
        </Button>
      </p>
    </>
  );
};

const MessageRequiringConfirmation = ({ title, content, confirmationButtonText }) => {
  const confirmed = useBooleanState(false);

  return (
    confirmed.value || (
      <Modal
        title={title}
        open={true}
        closable={false}
        footer={
          <Button type="primary" onClick={confirmed.setTrue}>
            {confirmationButtonText}
          </Button>
        }
      >
        {content}
      </Modal>
    )
  );
};

const ItemView = ({ title, items }) => {
  const { currentLanguage } = useLanguage();

  const itemsForComponent = items.map(({ field, label, value }, i) => ({
    key: i,
    label: field ? schemaTranslations[field]?.label?.[currentLanguage] : label,
    children: value ?? '',
  }));
  return <Descriptions className="itemView" title={title} items={itemsForComponent} column={1} size="small" />;
};

const EditableValues = ({
  title,
  componentData,
  viewComponent,
  editComponent,
  onUpdate,
  onDelete,
  editButtonLabel,
  updateButtonLabel,
  cancelButtonLabel,
  deleteButtonLabel,
  deleteConfirmationText,
}) => {
  const { lang } = useLanguage(accountTranslations, translations);
  const isEditing = useBooleanState(false);
  const { message } = App.useApp();

  const onFinish = async (newValues) => {
    onUpdate && (await onUpdate(newValues));
    message.success(lang('updateSuccessful'));
    isEditing.setFalse();
  };

  const submitLabel = updateButtonLabel ?? lang('update');

  const renderedViewComponent = viewComponent({ ...componentData });
  const renderedEditComponent = editComponent({ ...componentData, onFinish, submitLabel });

  return (
    <Card title={title}>
      <Row>
        <Col sm={24} md={18}>
          {!isEditing.value ? renderedViewComponent : renderedEditComponent}
          {isEditing.value && (
            <Button type="primary" onClick={isEditing.setFalse} style={{ marginLeft: '8px' }}>
              {cancelButtonLabel ?? lang('cancel')}
            </Button>
          )}
        </Col>
        {!isEditing.value && (
          <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={isEditing.setTrue}>
              {editButtonLabel ?? lang('edit')}
            </Button>
            {onDelete && (
              <Popconfirm
                description={deleteConfirmationText}
                onConfirm={onDelete}
                okText={lang('yes')}
                cancelText={lang('no')}
              >
                <Button>{deleteButtonLabel ?? lang('delete')}</Button>
              </Popconfirm>
            )}
          </Col>
        )}
      </Row>
    </Card>
  );
};

const OneTimeActionButton = ({ onClick, confirmation, children }) => {
  const clicked = useBooleanState(false);
  const { message } = App.useApp();

  const handleClick = async () => {
    clicked.setTrue();
    await onClick();
    message.success(confirmation);
  };
  return (
    <Button onClick={handleClick} disabled={clicked.value}>
      {children}
    </Button>
  );
};

const InfoIsFromEmployerMessage = () => {
  const { lang } = useLanguage(accountTranslations);
  return <Alert description={lang('infoIsFromEmployerMessage')} />;
};

const ExternalLink = ({ href, children }) => (
  <Typography.Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Typography.Link>
);

const markdownComponents = {
  p: ({ node, ...props }) => <>{props.children}</>,
  text: ({ node, ...props }) => <>{props.children}</>,
  a: ({ node, ...props }) => <ExternalLink {...props} />,
  strong: ({ node, ...props }) => <Typography.Text strong {...props} />,
};

const MarkdownText = ({ children }) => <ReactMarkdown components={markdownComponents}>{children}</ReactMarkdown>;

export {
  NavigateButton,
  Explainer,
  MessageRequiringConfirmation,
  ItemView,
  EditableValues,
  OneTimeActionButton,
  InfoIsFromEmployerMessage,
  ExternalLink,
  MarkdownText,
};
